import { CloseOutlined } from "@ant-design/icons";
import { Button, Empty, Form, Input, Modal, Table, Tag, message } from "antd";
import moment from "moment";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import {
  createCampaign,
  fetchCampaigns,
  whatsappError,
} from "../../services/whatsapp.service";

const WhatsAppCampaigns = () => {
  const queryClient = useQueryClient();
  const campaigns = useQuery("whatsapp/campaigns", fetchCampaigns);

  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();
  const [numberInput, setNumberInput] = useState("");
  const [numbers, setNumbers] = useState([]);
  const [creating, setCreating] = useState(false);

  const openCreate = () => {
    form.resetFields();
    setNumberInput("");
    setNumbers([]);
    setCreateOpen(true);
  };

  const addNumber = () => {
    const value = numberInput.trim();
    if (!value) return;
    if (numbers.includes(value)) {
      message.warning("That number is already in the list");
      setNumberInput("");
      return;
    }
    setNumbers((prev) => [...prev, value]);
    setNumberInput("");
  };

  const removeNumber = (value) => {
    setNumbers((prev) => prev.filter((n) => n !== value));
  };

  const handleCreate = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      return;
    }
    if (!numbers.length) {
      message.error("Add at least one phone number");
      return;
    }

    setCreating(true);
    try {
      await createCampaign({
        name: values.name.trim(),
        numbers_text: numbers.join("\n"),
      });
      message.success("Campaign saved");
      setCreateOpen(false);
      queryClient.invalidateQueries("whatsapp/campaigns");
    } catch (err) {
      message.error(whatsappError(err, "Could not save the campaign"));
    } finally {
      setCreating(false);
    }
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Numbers", dataIndex: "number_count", key: "number_count" },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => moment(v).format("DD-MM-YYYY HH:mm"),
    },
  ];

  return (
    <div
      style={{ borderRadius: "10px", color: "#616161" }}
      className="bg-white drop-shadow-md p-8 w-full mt-8"
    >
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="text-xl font-semibold text-gray-800">Campaigns</div>
          <div className="text-sm text-gray-500">
            Save a named list of phone numbers. To message them, check
            "Marketing" when creating a group in Chats and pick this campaign.
          </div>
        </div>
        <Button type="primary" onClick={openCreate}>
          New Campaign
        </Button>
      </div>

      <Table
        rowKey="_id"
        loading={campaigns.isLoading}
        dataSource={campaigns.data?.items || []}
        columns={columns}
        pagination={false}
        locale={{ emptyText: <Empty description="No campaigns yet" /> }}
      />

      <Modal
        title="New Campaign"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        confirmLoading={creating}
        okText="Save campaign"
        width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Campaign name"
            rules={[{ required: true, message: "Campaign name is required" }]}
          >
            <Input placeholder="e.g. Admissions 2026" maxLength={120} />
          </Form.Item>

          <Form.Item label="Phone numbers">
            <div style={{ display: "flex", gap: 12 }}>
              <Input
                placeholder="Enter a phone number"
                value={numberInput}
                onChange={(e) => setNumberInput(e.target.value)}
                onPressEnter={addNumber}
              />
              <Button onClick={addNumber}>Add</Button>
            </div>
            <div className="mt-2" style={{ maxHeight: 220, overflowY: "auto" }}>
              {numbers.length === 0 ? (
                <div className="text-xs text-gray-400 mt-1">
                  No numbers added yet
                </div>
              ) : (
                numbers.map((n) => (
                  <Tag
                    key={n}
                    closable
                    closeIcon={<CloseOutlined />}
                    onClose={() => removeNumber(n)}
                    style={{
                      marginBottom: 6,
                      display: "inline-flex",
                      alignItems: "center",
                      lineHeight: "normal",
                    }}
                  >
                    {n}
                  </Tag>
                ))
              )}
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WhatsAppCampaigns;
